import React, { useContext, useEffect, useMemo, useState } from 'react'
import AppContext from './context/appContext'
import logo from './l1.png'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Home = () => {
    const { getCustomHeaderRecords, getSalaries, getMaintenance, getShopMaintenance, getLoans, getUsers, getEmployees, getFlats, getShops, getElectricityBills, getMiscExpenses, getEvents, getReceipts } = useContext(AppContext)
    const history = useHistory()
    const [loading, setLoading] = useState(true)
    const [incomingCHR, setIncomingCHR] = useState([])
    const [expenseCHR, setExpenseCHR] = useState([])
    const [salaries, setSalaries] = useState([])
    const [maintenance, setMaintenance] = useState([])
    const [users, setUsers] = useState([])
    const [employees, setEmployees] = useState([])
    const [flats, setFlats] = useState([])
    const [shopMaintenance, setShopMaintenance] = useState([])
    const [loans, setLoans] = useState([])
    const [shops, setShops] = useState([])
    const [startDate, setStartDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [endDate, setEndDate] = useState(() => new Date());
    const [prevMonthClosing, setPrevMonthClosing] = useState(0);
    const [receiptsAll, setReceiptsAll] = useState([]);
    const [receiptsRange, setReceiptsRange] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true)
            const from = startDate ? new Date(startDate).toISOString() : undefined
            const to = endDate ? new Date(endDate).toISOString() : undefined
            const fmtLocalYMD = (d) => {
                if (!d) return new Date().toISOString().slice(0,10);
                const yy = d.getFullYear();
                const mm = String(d.getMonth()+1).padStart(2,'0');
                const dd = String(d.getDate()).padStart(2,'0');
                return `${yy}-${mm}-${dd}`;
            }
            const [inChr, exChr, sal, maint, shopMaint, loanList, us, emps, fls, shps, bills, misc, events, allReceipts, rangeReceipts, billsAllRes, miscAllRes, eventsAllRes] = await Promise.all([
                getCustomHeaderRecords({ headerType: 'Incoming', from, to }),
                getCustomHeaderRecords({ headerType: 'Expense', from, to }),
                getSalaries({ from, to }),
                getMaintenance({ from, to }),
                getShopMaintenance({ from, to }),
                getLoans({ from, to }),
                getUsers(),
                getEmployees(),
                getFlats(),
                getShops(),
                getElectricityBills({ from, to }),
                getMiscExpenses({ from, to }),
                getEvents({ from, to }),
                getReceipts(), // all time for balance and previous month closing
                getReceipts({ from: fmtLocalYMD(startDate), to: fmtLocalYMD(endDate) }), // range-bound for KPIs
                getElectricityBills(), // all-time for payables
                getMiscExpenses(),     // all-time for payables
                getEvents()            // all-time for outstandings
            ])
            setIncomingCHR(inChr || [])
            setExpenseCHR(exChr || [])
            setSalaries(sal || [])
            setMaintenance(maint || [])
            setShopMaintenance(shopMaint || [])
            setLoans(loanList || [])
            setUsers(us || [])
            setEmployees(emps || [])
            setFlats(fls || [])
            setShops(shps || [])
            setBills(bills || [])
            setMisc(misc || [])
            setEventsList(events || [])
            setReceiptsAll(Array.isArray(allReceipts) ? allReceipts : [])
            setReceiptsRange(Array.isArray(rangeReceipts) ? rangeReceipts : [])
            setBillsAll(billsAllRes || [])
            setMiscAll(miscAllRes || [])
            setEventsAll(eventsAllRes || [])
            setLoading(false)
        })()
    }, [getCustomHeaderRecords, getSalaries, getMaintenance, getShopMaintenance, getLoans, getUsers, getEmployees, getFlats, getShops, getElectricityBills, getMiscExpenses, getEvents, getReceipts, startDate, endDate])

    useEffect(() => {
        // Compute previous month balance from receipts for the month preceding selected range
        if (!Array.isArray(receiptsAll)) return;
        const base = startDate ? new Date(startDate) : new Date();
        const prevStart = new Date(base.getFullYear(), base.getMonth() - 1, 1, 0, 0, 0, 0);
        const prevEnd = new Date(base.getFullYear(), base.getMonth(), 0, 23, 59, 59, 999);
        let received = 0, paid = 0;
        for (const r of receiptsAll) {
            const dt = new Date(r?.createdAt || r?.dateOfCreation || 0);
            if (isNaN(dt.getTime())) continue;
            if (dt >= prevStart && dt <= prevEnd) {
                if (r?.type === 'Recieved') received += Number(r?.amount || 0);
                if (r?.type === 'Paid') paid += Number(r?.amount || 0);
            }
        }
        setPrevMonthClosing(received - paid);
    }, [receiptsAll, startDate])

    const fmt = (n) => Number(n || 0).toLocaleString('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 0 })
    const count = (arr = [], status) => (Array.isArray(arr) ? arr.filter(m => m?.status === status).length : 0)

    const [bills, setBills] = useState([])
    const [misc, setMisc] = useState([])
    const [eventsList, setEventsList] = useState([])
    const [billsAll, setBillsAll] = useState([])
    const [miscAll, setMiscAll] = useState([])
    const [eventsAll, setEventsAll] = useState([])

    const totals = useMemo(() => {
        const paidValue = (m) => (m?.status === 'Paid' ? Number((m?.paidAmount && m.paidAmount > 0) ? m.paidAmount : (m?.amount || 0)) : 0)
        const paidFromMonths = (rec) => (rec?.month || []).reduce((s,m)=> s + paidValue(m), 0)
        const dueFromMonths = (rec) => (rec?.month || []).reduce((s,m)=> s + (m?.status==='Due' ? Number(m.amount||0) : 0), 0)
        const sumPaid = (months=[]) => (months || []).reduce((s,m)=> s + paidValue(m), 0)
        const sumDue  = (months=[]) => (months || []).reduce((s,m)=> s + (m?.status==='Due'  ? Number(m.amount||0) : 0), 0)
        const isRec = (r) => !!r?.header?.recurring
        const oneOffPaid = (r) => {
            const months = Array.isArray(r?.month) ? r.month : []
            return months.length > 0 ? sumPaid(months) : Number(r?.amount || 0)
        }

        const incomingPaid = (incomingCHR || []).reduce((a, r) => a + (isRec(r) ? paidFromMonths(r) : oneOffPaid(r)), 0)
        const incomingDue  = (incomingCHR || []).reduce((a, r) => a + (isRec(r) ? dueFromMonths(r) : 0), 0)

        const maintPaid        = (maintenance || []).reduce((a, m) => a + sumPaid(m.month), 0)
        const shopMaintPaid    = (shopMaintenance || []).reduce((a, m) => a + sumPaid(m.month), 0)
        const maintDue         = (maintenance || []).reduce((a, m) => a + sumDue(m.month), 0)
        const maintOutstandingDue = (maintenance || []).reduce((a, m) => a + ((m?.outstanding?.status === 'Due') ? Number(m.outstanding.amount || 0) : 0), 0)
        const shopMaintDue     = (shopMaintenance || []).reduce((a, m) => a + sumDue(m.month), 0)

        const loanPending      = (loans || []).reduce((a, l) => a + (l.status === 'Pending' ? Number(l.amount||0) : 0), 0)
        const loanPaid         = (loans || []).reduce((a, l) => a + (l.status === 'Paid'    ? Number(l.amount||0) : 0), 0)

        const salaryPaid   = (salaries || []).reduce((a, s) => a + paidFromMonths(s), 0)
        const chrExpPaid   = (expenseCHR || []).reduce((a, r) => a + (isRec(r) ? paidFromMonths(r) : oneOffPaid(r)), 0)
        const salaryDue    = (salaries || []).reduce((a, s) => a + dueFromMonths(s), 0)
        const chrExpDue    = (expenseCHR || []).reduce((a, r) => a + (isRec(r) ? dueFromMonths(r) : 0), 0)
        // Employee Payables (schema-level)
        const employeeMonthlyPayables = (employees || []).reduce((a,e)=> a + Number(e?.salaryRecord?.monthlyPayables?.amount || 0), 0)
        const employeePayables = (employees || []).reduce((a,e)=> a + Number(e?.salaryRecord?.Payables?.amount || 0), 0)
        const elecMonthlyPayables = (billsAll || []).reduce((a,b)=> a + Number(b?.BillRecord?.monthlyPayables?.amount || 0), 0)
        const miscOutstanding = (miscAll || []).reduce((a,m)=> a + Number(m?.amount || 0), 0)
        // Payables should be all-time; prefer schema-level aggregates
        const expenseDue   = employeeMonthlyPayables + employeePayables + elecMonthlyPayables + miscOutstanding

        // Paid amounts captured on flat/shop schemas via Pay pages
        const flatsPaidAmount = (flats || []).reduce((a,f)=> a + Number(f?.maintenanceRecord?.paidAmount || 0), 0)
        const shopsPaidAmount = (shops || []).reduce((a,s)=> a + Number(s?.maintenanceRecord?.paidAmount || 0), 0)
        const flatsAdvanceAmount = (flats || []).reduce((a,f)=> a + Number(f?.maintenanceRecord?.AdvanceMaintenance?.amount || 0), 0)
        const shopsAdvanceAmount = (shops || []).reduce((a,s)=> a + Number(s?.maintenanceRecord?.AdvanceMaintenance?.amount || 0), 0)
        const elecPaid = (bills || []).reduce((a,b)=> a + Number(b?.BillRecord?.paidAmount || 0), 0)
        const miscPaid = (misc || []).reduce((a,m)=> a + Number(m?.paidAmount || 0), 0)
        const eventsPaid = (eventsList || []).reduce((a,e)=> a + Number(e?.paidAmount || 0), 0)
        const totalIncomingReceived = incomingPaid + maintPaid + shopMaintPaid + flatsPaidAmount + shopsPaidAmount + eventsPaid
        const totalExpensePaid = salaryPaid + chrExpPaid + loanPaid + elecPaid + miscPaid
        // Subtract remaining employee loan and only non-loan employee payments
        const employeeLoanAmtForBalance = (employees || []).reduce((a,e)=> a + Number(e?.salaryRecord?.loan?.amount || 0), 0)
        const employeesPaidAmount = (employees || []).reduce((a,e)=> a + Number(e?.salaryRecord?.paidAmount || 0), 0)
        const employeesLoanPaidAmount = (employees || []).reduce((a,e)=> a + Number(e?.salaryRecord?.loan?.paidAmount || 0), 0)
        const employeesNonLoanPaid = Math.max(0, employeesPaidAmount - employeesLoanPaidAmount)
        // Current balance independent of date range: use all receipts
        const totalReceivedAll = (receiptsAll || []).reduce((s,r)=> s + (r?.type === 'Recieved' ? Number(r?.amount||0) : 0), 0)
        const totalPaidAllAll = (receiptsAll || []).reduce((s,r)=> s + (r?.type === 'Paid' ? Number(r?.amount||0) : 0), 0)
        const advanceAmount = flatsAdvanceAmount + shopsAdvanceAmount
        const currentBalance = totalReceivedAll - totalPaidAllAll

        // Outstandings from flat/shop schemas
        const flatSchemaOut = (flats || []).reduce((a,f)=> {
            const mr = f?.maintenanceRecord || {}
            return a
                + Number(mr?.Outstandings?.amount || 0)
                + Number(mr?.OtherOutstandings?.amount || 0)
                + Number(mr?.monthlyOutstandings?.amount || 0)
        }, 0)
        const shopSchemaOut = (shops || []).reduce((a,s)=> {
            const mr = s?.maintenanceRecord || {}
            return a
                + Number(mr?.Outstandings?.amount || 0)
                + Number(mr?.OtherOutstandings?.amount || 0)
                + Number(mr?.monthlyOutstandings?.amount || 0)
        }, 0)
        // Employee loan included in Outstandings
        const employeeLoanAmt = (employees || []).reduce((a,e)=> a + Number(e?.salaryRecord?.loan?.amount || 0), 0)
        const eventsOutstanding = (eventsAll || []).reduce((a,e)=> a + Number(e?.amount || 0), 0)
        // Outstandings should be all-time; rely on schema-level outstandings plus events
        const incomingOutstanding = flatSchemaOut + shopSchemaOut + eventsOutstanding

        // Use receipts-based totals for KPIs (independent of date range)
        return {
            currentBalance,
            incomingDue: incomingOutstanding,
            // KPIs as per selected date interval: use range-bound receipts
            totalIncomingReceived: (receiptsRange || []).reduce((s,r)=> s + (r?.type === 'Recieved' ? Number(r?.amount||0) : 0), 0),
            totalExpensePaid: (receiptsRange || []).reduce((s,r)=> s + (r?.type === 'Paid' ? Number(r?.amount||0) : 0), 0),
            maintPaid: maintPaid + shopMaintPaid,
            shopMaintPaid,
            expenseDue,
            loanAmount: employeeLoanAmt,
            advanceAmount
        }
    }, [incomingCHR, expenseCHR, salaries, maintenance, shopMaintenance, loans, flats, shops, employees, bills, misc, eventsList, receiptsAll, receiptsRange])

    const entityCounts = useMemo(() => ({
        flats: Array.isArray(flats) ? flats.length : 0,
        employees: Array.isArray(employees) ? employees.length : 0,
        users: Array.isArray(users) ? users.length : 0,
        shops: Array.isArray(shops) ? shops.length : 0,
    }), [flats, employees, users, shops])

    return (
        <div className="container-fluid py-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                {/* <h1 className="display-6 m-0 fw-bold">Dashboard</h1> */}
                <button
                    className="btn btn-outline-primary"
                    onClick={() => {
                        const qs = new URLSearchParams();
                        if (startDate) qs.set('from', new Date(startDate).toISOString());
                        if (endDate) qs.set('to', new Date(endDate).toISOString());
                        history.push(`/pdf/dashboard${qs.toString() ? `?${qs.toString()}` : ''}`);
                    }}
                >Print PDF</button>
            </div>
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="display-5 m-0" style={{ fontWeight: 900 }}>Admin Dashboard</h1>
                    <img src={logo} alt="Lakhani Towers" style={{ height: 60 }} />
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                    <DatePicker className="form-control" selected={startDate} onChange={(d)=>setStartDate(d)} placeholderText="Start date" dateFormat="dd/MM/yy" maxDate={endDate || new Date()} />
                    <DatePicker className="form-control" selected={endDate} onChange={(d)=>setEndDate(d)} placeholderText="End date" dateFormat="dd/MM/yy" minDate={startDate} maxDate={new Date()} />
                    <button className="btn btn-outline-secondary" onClick={()=>{
                        const now = new Date();
                        setStartDate(new Date(now.getFullYear(), now.getMonth(), 1));
                        setEndDate(new Date());
                    }}>Reset</button>
                </div>
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "40vh" }}>
                    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                </div>
            ) : (
                <>
                <div className="row g-3">
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#1d976c,#93f9b9)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Current Balance</h6>
                                    <span>💼</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.currentBalance)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#136a8a,#267871)' }}>
                            <div className="card-body" style={{ cursor: 'pointer' }} onClick={()=>{
                                const qs = new URLSearchParams();
                                if (startDate) qs.set('from', new Date(startDate).toISOString());
                                if (endDate) qs.set('to', new Date(endDate).toISOString());
                                window.open(`/pdf/balance-sheet${qs.toString() ? `?${qs.toString()}` : ''}`, '_blank');
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Balance Sheet</h6>
                                    <span>📅</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(prevMonthClosing)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#f7971e,#ffd200)' }}>
                            <div className="card-body" style={{ cursor: 'pointer' }} onClick={()=>history.push('/dashboard/flats?status=due')}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Outstandings</h6>
                                    <span>⏳</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.incomingDue)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#8E2DE2,#4A00E0)' }}>
                            <div className="card-body" style={{ cursor: 'pointer' }} onClick={()=>history.push('/dashboard/employees?status=payables')}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Payables</h6>
                                    <span>📤</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.expenseDue)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#00c6ff,#0072ff)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Total Amount Received</h6>
                                    <span>📥</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.totalIncomingReceived)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#f85032,#e73827)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Total Expenses</h6>
                                    <span>💸</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.totalExpensePaid)}</div>
                            </div>
                        </div>
                    </div>
                    {/* <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#f85032,#e73827)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Paid Maintenance</h6>
                                    <span>🧾</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.maintPaid)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#ff7e5f,#feb47b)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Paid Shop Maintenance</h6>
                                    <span>🧾</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.shopMaintPaid)}</div>
                            </div>
                        </div>
                    </div> */}
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#11998e,#38ef7d)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Total Flats</h6>
                                    <span>🏢</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{entityCounts.flats}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#FF512F,#DD2476)' }}>
                            <div className="card-body" style={{ cursor: 'pointer' }} onClick={()=>history.push('/dashboard/employees?status=due')}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Loan Amount</h6>
                                    <span>💳</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.loanAmount)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#4b6cb7,#182848)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Total Shops</h6>
                                    <span>🏬</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{entityCounts.shops}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#00b09b,#96c93d)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Advance Amount</h6>
                                    <span>💠</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{fmt(totals.advanceAmount)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#7F00FF,#E100FF)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Total Employees</h6>
                                    <span>👷</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{entityCounts.employees}</div>
                            </div>
                        </div>
                    </div>
                    {/* <div className="col-md-6 col-xl-3">
                        <div className="card border-0 shadow-sm text-white" style={{ background: 'linear-gradient(135deg,#00b09b,#96c93d)' }}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-1 fw-bold">Total Users</h6>
                                    <span>👤</span>
                                </div>
                                <div className="fs-4 fw-bold mt-2">{entityCounts.users}</div>
                            </div>
                        </div>
                    </div> */}
            </div>
        </>
            )}
            </div>
        </div>
    )
}

export default Home